import { useCallback, useEffect, useState } from 'react';
import {
  adminCreateMediator,
  adminCreatePartnerUser,
  adminCreateSource,
  adminListMediators,
  adminListPartnerUsers,
  adminListSources,
  adminPatchMediator,
  adminPatchPartnerUser,
  adminPatchSource,
  adminResetMediatorPassword,
  adminResetPartnerPassword,
  adminRotateSourceKey,
  EngineError,
  impersonate,
  type AdminMediator,
  type AdminPartnerUser,
  type AdminSource,
  type ResetIssued,
  adminListPractices,
  adminCreatePractice,
  adminAttachPracticeSource,
  type AdminPractice,
} from '../../api/engineDashboard';
import type { Principal } from '../../types/dashboard';

interface Props {
  token: string;
  selfId: string | null;
  onImpersonate: (session: { token: string; expires_in: number; principal: Principal }) => void;
  onUnauthorized: () => void;
}

type Tab = 'people' | 'partners' | 'sources' | 'practices';

/** Admin area: accounts + sources. Everything here is audited server-side;
 * new accounts get a one-hour reset link instead of a password. */
export function AdminPage({ token, selfId, onImpersonate, onUnauthorized }: Props) {
  const [tab, setTab] = useState<Tab>('people');
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1 text-sm">
        {(['people', 'partners', 'sources', 'practices'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={[
              'rounded-md px-3 py-1.5 font-medium',
              tab === t ? 'bg-brand-subtle text-brand' : 'text-gray-600 hover:bg-surface-muted',
            ].join(' ')}
          >
            {t === 'people' ? 'People' : t === 'partners' ? 'Partner logins' : t === 'practices' ? 'Practices' : 'Sources'}
          </button>
        ))}
      </div>
      {tab === 'people' && <PeopleTab token={token} selfId={selfId} onImpersonate={onImpersonate} onUnauthorized={onUnauthorized} />}
      {tab === 'partners' && <PartnersTab token={token} onUnauthorized={onUnauthorized} />}
      {tab === 'sources' && <SourcesTab token={token} onUnauthorized={onUnauthorized} />}
      {tab === 'practices' && <PracticesTab token={token} onUnauthorized={onUnauthorized} />}
    </div>
  );
}

function useGuard(onUnauthorized: () => void) {
  return useCallback(
    (err: unknown): string => {
      if (err instanceof EngineError && err.status === 401) {
        onUnauthorized();
        return '';
      }
      return err instanceof EngineError ? err.message : 'Something went wrong.';
    },
    [onUnauthorized],
  );
}

function ResetNotice({ reset }: { reset: ResetIssued | null }) {
  if (!reset) return null;
  return (
    <div className="mt-2 rounded-md bg-brand-subtle p-2 text-xs">
      {reset.reset_emailed ? 'Reset link emailed.' : 'Email not configured — copy the link:'}
      <div className="mt-1 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate">{reset.reset_link}</code>
        <button
          type="button"
          className="btn-secondary !py-0.5 !text-xs"
          onClick={() => void navigator.clipboard?.writeText(reset.reset_link).catch(() => {})}
        >
          Copy
        </button>
      </div>
      <div className="mt-1 text-gray-500">Valid for one hour, single use.</div>
    </div>
  );
}

/* ════════════════════════ People ════════════════════════ */

function PeopleTab({ token, selfId, onImpersonate, onUnauthorized }: {
  token: string;
  selfId: string | null;
  onImpersonate: Props['onImpersonate'];
  onUnauthorized: () => void;
}) {
  const guard = useGuard(onUnauthorized);
  const [mediators, setMediators] = useState<AdminMediator[] | null>(null);
  const [sources, setSources] = useState<AdminSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resets, setResets] = useState<Record<string, ResetIssued>>({});
  const [expanded, setExpanded] = useState<string | null>(null);

  // Create form
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'mediator' | 'admin' | 'clerk' | 'admin_mediator'>('mediator');
  const [judgeRO, setJudgeRO] = useState(false);
  const [zoomEmail, setZoomEmail] = useState('');
  const [newSources, setNewSources] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [createdReset, setCreatedReset] = useState<ResetIssued | null>(null);

  const load = useCallback(() => {
    Promise.all([adminListMediators(token), adminListSources(token)])
      .then(([m, s]) => {
        setMediators(m.mediators);
        setSources(s.sources);
      })
      .catch((err: unknown) => setError(guard(err)));
  }, [token, guard]);
  useEffect(load, [load]);

  const create = async () => {
    setCreating(true);
    setError(null);
    setCreatedReset(null);
    try {
      const res = await adminCreateMediator(token, {
        email, display_name: name, role, judge_read_only: role === 'clerk' && judgeRO,
        source_org_ids: [...newSources],
        ...(zoomEmail.trim() ? { zoom_user_id: zoomEmail.trim() } : {}),
      });
      setCreatedReset({ reset_link: res.reset_link, reset_emailed: res.reset_emailed });
      setEmail(''); setName(''); setRole('mediator'); setJudgeRO(false); setNewSources(new Set()); setZoomEmail('');
      load();
    } catch (err) {
      setError(guard(err));
    } finally {
      setCreating(false);
    }
  };

  const patch = async (id: string, body: Record<string, unknown>) => {
    setError(null);
    try {
      await adminPatchMediator(token, id, body);
      load();
    } catch (err) {
      setError(guard(err));
    }
  };

  const doImpersonate = async (m: AdminMediator) => {
    if (!window.confirm(`View Levelheaded as ${m.display_name}? Everything you do is audited under both identities.`)) return;
    try {
      const res = await impersonate(token, m.id);
      onImpersonate(res);
    } catch (err) {
      setError(guard(err));
    }
  };

  return (
    <div className="space-y-4">
      {error && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{error}</div>}

      <section className="card p-4">
        <div className="section-label">Add a team member</div>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
          <select className="rounded-md border border-surface-border px-3 py-2 text-sm" value={role} onChange={(e) => setRole(e.target.value as never)}>
            <option value="mediator">Mediator</option>
            <option value="admin_mediator">Admin Mediator</option>
            <option value="admin">Admin</option>
            <option value="clerk">Clerk</option>
          </select>
          {role === 'clerk' && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={judgeRO} onChange={(e) => setJudgeRO(e.target.checked)} />
              Judge (read-only)
            </label>
          )}
          {role !== 'clerk' && (
            <input
              className="rounded-md border border-surface-border px-3 py-2 text-sm"
              placeholder="Zoom email (meeting host — must be on the Levelheaded Zoom account)"
              type="email"
              value={zoomEmail}
              onChange={(e) => setZoomEmail(e.target.value)}
            />
          )}
        </div>
        <div className="mt-2 text-xs font-medium text-gray-600">Assigned sources</div>
        <div className="mt-1 flex flex-wrap gap-3">
          {sources.map((s) => (
            <label key={s.id} className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={newSources.has(s.id)}
                onChange={(e) => {
                  const next = new Set(newSources);
                  if (e.target.checked) next.add(s.id); else next.delete(s.id);
                  setNewSources(next);
                }}
              />
              {s.name}
            </label>
          ))}
        </div>
        <button type="button" className="btn-secondary mt-3" disabled={creating || !email || !name} onClick={() => void create()}>
          {creating ? 'Creating…' : 'Create & send set-password link'}
        </button>
        <ResetNotice reset={createdReset} />
      </section>

      <section className="card">
        <div className="border-b border-surface-border p-3"><div className="section-label">Team</div></div>
        {mediators === null ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : (
          <ul className="divide-y divide-surface-border">
            {mediators.map((m) => (
              <li key={m.id} className="px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <span className={['text-sm font-medium', m.is_active ? '' : 'text-gray-400 line-through'].join(' ')}>{m.display_name}</span>
                    <span className="ml-2 text-xs text-gray-500">{m.email}</span>
                    <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                      {m.role}{m.judge_read_only ? ' · read-only' : ''}
                    </span>
                    <div className="mt-0.5 text-xs text-gray-400">
                      {m.sources.length ? m.sources.map((s) => s.name).join(', ') : 'No sources assigned'}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                    {m.is_active && m.id !== selfId && (
                      <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => void doImpersonate(m)}>
                        Impersonate
                      </button>
                    )}
                    {m.is_active && (
                      <button
                        type="button"
                        className="btn-secondary !py-1 !text-xs"
                        onClick={() =>
                          void adminResetMediatorPassword(token, m.id)
                            .then((r) => setResets((prev) => ({ ...prev, [m.id]: r })))
                            .catch((err) => setError(guard(err)))
                        }
                      >
                        Reset password
                      </button>
                    )}
                    <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setExpanded(expanded === m.id ? null : m.id)}>
                      {expanded === m.id ? 'Close' : 'Edit'}
                    </button>
                    {m.id !== selfId && (
                      <button
                        type="button"
                        className="btn-secondary !py-1 !text-xs"
                        onClick={() => {
                          if (m.is_active && !window.confirm(`Deactivate ${m.display_name}? They will no longer be able to sign in.`)) return;
                          void patch(m.id, { is_active: !m.is_active });
                        }}
                      >
                        {m.is_active ? 'Deactivate' : 'Reactivate'}
                      </button>
                    )}
                  </div>
                </div>
                <ResetNotice reset={resets[m.id] ?? null} />
                {expanded === m.id && (
                  <div className="mt-2 rounded-md bg-surface-muted p-2">
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <label className="flex items-center gap-1.5">
                        Role
                        <select
                          className="rounded-md border border-surface-border px-2 py-1"
                          value={m.role}
                          onChange={(e) => void patch(m.id, { role: e.target.value })}
                        >
                          <option value="mediator">Mediator</option>
                          <option value="admin_mediator">Admin Mediator</option>
                          <option value="admin">Admin</option>
                          <option value="clerk">Clerk</option>
                        </select>
                      </label>
                      {m.role === 'clerk' && (
                        <label className="flex items-center gap-1.5">
                          <input type="checkbox" checked={m.judge_read_only} onChange={(e) => void patch(m.id, { judge_read_only: e.target.checked })} />
                          Judge (read-only)
                        </label>
                      )}
                    </div>
                    <div className="mt-2 text-xs font-medium text-gray-600">
                      Zoom email (meeting host)
                    </div>
                    <ZoomIdEditor
                      current={m.zoom_user_id}
                      onSave={(v) => void patch(m.id, { zoom_user_id: v })}
                    />
                    <div className="mt-2 text-xs font-medium text-gray-600">Sources</div>
                    <div className="mt-1 flex flex-wrap gap-3">
                      {sources.map((s) => {
                        const has = m.sources.some((x) => x.org_id === s.id);
                        return (
                          <label key={s.id} className="flex items-center gap-1.5 text-xs">
                            <input
                              type="checkbox"
                              checked={has}
                              onChange={() => {
                                const next = has
                                  ? m.sources.filter((x) => x.org_id !== s.id).map((x) => x.org_id)
                                  : [...m.sources.map((x) => x.org_id), s.id];
                                void patch(m.id, { source_org_ids: next });
                              }}
                            />
                            {s.name}
                          </label>
                        );
                      })}
                    </div>
                    {m.role === 'clerk' && m.sources.length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs font-medium text-gray-600" title="Comma-separated; blank = all divisions">
                          Division scope (clerks/judges only — blank = all)
                        </div>
                        {m.sources.map((src) => (
                          <label key={src.org_id} className="mt-1 flex items-center gap-2 text-xs">
                            <span className="w-40 shrink-0 truncate text-gray-500">{src.name}</span>
                            <input
                              type="text"
                              className="w-full rounded-md border border-surface-border px-2 py-0.5 text-xs"
                              defaultValue={(src.divisions ?? []).join(', ')}
                              placeholder="all divisions"
                              onBlur={(e) => {
                                const next = e.target.value.split(',').map((d) => d.trim()).filter(Boolean);
                                const current = src.divisions ?? [];
                                if (next.join('|') === current.join('|')) return;
                                const map: Record<string, string[]> = {};
                                for (const x of m.sources) {
                                  const val = x.org_id === src.org_id ? next : (x.divisions ?? []);
                                  if (val.length) map[x.org_id] = val;
                                }
                                void patch(m.id, {
                                  source_org_ids: m.sources.map((x) => x.org_id),
                                  assignment_divisions: map,
                                });
                              }}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/** Zoom identity for meeting hosting — booked sessions are hosted as this
 * user, which is what makes RTMS + the in-meeting sidebar fire for the
 * assigned mediator. Must be a user on the Levelheaded Zoom account. */
function ZoomIdEditor({ current, onSave }: { current: string | null; onSave: (v: string) => void }) {
  const [value, setValue] = useState(current ?? '');
  return (
    <div className="mt-1 flex items-center gap-1.5">
      <input
        className="flex-1 rounded-md border border-surface-border px-2 py-1 text-xs"
        placeholder="name@levelheadedzoom.com (blank = app owner hosts)"
        type="email"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      />
      <button
        type="button"
        className="btn-secondary !py-1 !text-xs"
        disabled={value.trim() === (current ?? '')}
        onClick={() => onSave(value.trim())}
      >
        Save
      </button>
    </div>
  );
}

/* ════════════════════════ Partners ════════════════════════ */

function PartnersTab({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const guard = useGuard(onUnauthorized);
  const [partners, setPartners] = useState<AdminPartnerUser[] | null>(null);
  const [sources, setSources] = useState<AdminSource[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resets, setResets] = useState<Record<string, ResetIssued>>({});
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [orgId, setOrgId] = useState('');
  const [createdReset, setCreatedReset] = useState<ResetIssued | null>(null);

  const load = useCallback(() => {
    Promise.all([adminListPartnerUsers(token), adminListSources(token)])
      .then(([p, s]) => {
        setPartners(p.partner_users);
        setSources(s.sources);
        setOrgId((prev) => prev || s.sources[0]?.id || '');
      })
      .catch((err: unknown) => setError(guard(err)));
  }, [token, guard]);
  useEffect(load, [load]);

  return (
    <div className="space-y-4">
      {error && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{error}</div>}
      <section className="card p-4">
        <div className="section-label">Add a partner login</div>
        <p className="mt-1 text-xs text-gray-500">
          Partner logins see only their source&rsquo;s case statuses and resolution agreements — never
          case detail, notes, or messages.
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          <select className="rounded-md border border-surface-border px-3 py-2 text-sm" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn-secondary mt-3"
          disabled={!email || !name || !orgId}
          onClick={() =>
            void adminCreatePartnerUser(token, { org_id: orgId, email, display_name: name })
              .then((res) => {
                setCreatedReset({ reset_link: res.reset_link, reset_emailed: res.reset_emailed });
                setEmail(''); setName('');
                load();
              })
              .catch((err) => setError(guard(err)))
          }
        >
          Create &amp; send set-password link
        </button>
        <ResetNotice reset={createdReset} />
      </section>

      <section className="card">
        <div className="border-b border-surface-border p-3"><div className="section-label">Partner logins</div></div>
        {partners === null ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : partners.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">No partner logins yet.</p>
        ) : (
          <ul className="divide-y divide-surface-border">
            {partners.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <span className={['text-sm font-medium', p.is_active ? '' : 'text-gray-400 line-through'].join(' ')}>{p.display_name}</span>
                  <span className="ml-2 text-xs text-gray-500">{p.email}</span>
                  <div className="mt-0.5 text-xs text-gray-400">{p.org_name}</div>
                </div>
                <div className="flex shrink-0 gap-1.5">
                  {p.is_active && (
                    <button
                      type="button"
                      className="btn-secondary !py-1 !text-xs"
                      onClick={() =>
                        void adminResetPartnerPassword(token, p.id)
                          .then((r) => setResets((prev) => ({ ...prev, [p.id]: r })))
                          .catch((err) => setError(guard(err)))
                      }
                    >
                      Reset password
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-secondary !py-1 !text-xs"
                    onClick={() => void adminPatchPartnerUser(token, p.id, { is_active: !p.is_active }).then(load).catch((err) => setError(guard(err)))}
                  >
                    {p.is_active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </div>
                <div className="w-full"><ResetNotice reset={resets[p.id] ?? null} /></div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/* ════════════════════════ Sources ════════════════════════ */

function SourcesTab({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const guard = useGuard(onUnauthorized);
  const [sourcesList, setSourcesList] = useState<AdminSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newKeys, setNewKeys] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<'pm_firm' | 'court' | 'campus' | 'keel_direct'>('pm_firm');
  const [slug, setSlug] = useState('');

  const load = useCallback(() => {
    adminListSources(token)
      .then((s) => setSourcesList(s.sources))
      .catch((err: unknown) => setError(guard(err)));
  }, [token, guard]);
  useEffect(load, [load]);

  const patchSource = (id: string, body: Record<string, unknown>) =>
    adminPatchSource(token, id, body).then(load).catch((err) => setError(guard(err)));

  return (
    <div className="space-y-4">
      {error && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{error}</div>}
      <section className="card p-4">
        <div className="section-label">Add a referral source</div>
        <div className="mt-2 grid gap-2 sm:grid-cols-3">
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Name (e.g. Jefferson County Court)" value={name} onChange={(e) => setName(e.target.value)} />
          <select className="rounded-md border border-surface-border px-3 py-2 text-sm" value={type} onChange={(e) => setType(e.target.value as never)}>
            <option value="pm_firm">PM firm</option>
            <option value="court">Court</option>
            <option value="campus">Campus</option>
            <option value="keel_direct">Levelheaded Direct</option>
          </select>
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Intake slug (courts, e.g. jeffco)" value={slug} onChange={(e) => setSlug(e.target.value)} />
        </div>
        <button
          type="button"
          className="btn-secondary mt-3"
          disabled={!name}
          onClick={() =>
            void adminCreateSource(token, { name, source_type: type, ...(slug ? { intake_slug: slug } : {}) })
              .then((res) => {
                setNewKeys((prev) => ({ ...prev, [res.id]: res.api_key }));
                setName(''); setSlug('');
                load();
              })
              .catch((err) => setError(guard(err)))
          }
        >
          Create source
        </button>
      </section>

      <section className="card">
        <div className="border-b border-surface-border p-3"><div className="section-label">Sources</div></div>
        {sourcesList === null ? (
          <p className="p-4 text-sm text-gray-500">Loading…</p>
        ) : (
          <ul className="divide-y divide-surface-border">
            {sourcesList.map((s) => {
              const billing = s.billing_config ?? {};
              const intake = s.intake_config ?? {};
              return (
                <li key={s.id} className="px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="text-sm font-medium">{s.name}</span>
                      <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">{s.source_type}</span>
                      <div className="mt-0.5 font-mono text-xs text-gray-400">API key: {newKeys[s.id] ?? s.api_key}</div>
                      {newKeys[s.id] && (
                        <div className="text-xs text-amber-700">Shown once — copy it now.</div>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                        {expanded === s.id ? 'Close' : 'Edit'}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary !py-1 !text-xs"
                        onClick={() => {
                          if (!window.confirm(`Rotate the API key for ${s.name}? The old key stops working IMMEDIATELY — any integration using it must be updated.`)) return;
                          void adminRotateSourceKey(token, s.id)
                            .then((r) => setNewKeys((prev) => ({ ...prev, [s.id]: r.api_key })))
                            .catch((err) => setError(guard(err)));
                        }}
                      >
                        Rotate key…
                      </button>
                    </div>
                  </div>
                  {expanded === s.id && (
                    <SourceEditor
                      source={s}
                      billing={billing}
                      intake={intake}
                      onSave={(body) => void patchSource(s.id, body)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function SourceEditor({ source, billing, intake, onSave }: {
  source: AdminSource;
  billing: Record<string, unknown>;
  intake: Record<string, unknown>;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [slug, setSlug] = useState(String(intake.slug ?? ''));
  const [feeCents, setFeeCents] = useState(String(billing.party_fee_cents ?? ''));
  const [trigger, setTrigger] = useState(String(billing.invoice_trigger ?? 'on_booking_confirmation'));
  const [connect, setConnect] = useState(String(billing.stripe_connect_account_id ?? ''));
  const [smsNumber, setSmsNumber] = useState(String(source.sms_from_number ?? ''));
  const [smsIntake, setSmsIntake] = useState(intake.sms_intake_enabled === true);
  const [divisions, setDivisions] = useState(
    Array.isArray(intake.divisions) ? (intake.divisions as string[]).join(', ') : '',
  );
  // Default session lengths per source (Danny, 2026-10-15): consultation is
  // nested under intake_config.consult; mediation is a flat key. A mediator
  // can override either when they send a scheduling link.
  const [consultMin, setConsultMin] = useState(
    String((intake.consult as Record<string, unknown> | undefined)?.duration_minutes ?? ''),
  );
  const [mediationMin, setMediationMin] = useState(String(intake.mediation_duration_minutes ?? ''));

  return (
    <div className="mt-2 grid gap-2 rounded-md bg-surface-muted p-2 text-xs sm:grid-cols-2">
      <label>
        Intake slug (public /intake/&lt;slug&gt;)
        <input className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={slug} onChange={(e) => setSlug(e.target.value)} />
      </label>
      <label>
        Party fee (cents, courts)
        <input className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={feeCents} onChange={(e) => setFeeCents(e.target.value)} />
      </label>
      <label>
        Invoice trigger
        <select className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={trigger} onChange={(e) => setTrigger(e.target.value)}>
          <option value="on_booking_confirmation">On booking confirmation</option>
          <option value="manual">Manual</option>
          <option value="none">None</option>
        </select>
      </label>
      <label>
        Stripe Connect account (acct_…)
        <input className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={connect} onChange={(e) => setConnect(e.target.value)} />
      </label>
      <label>
        Dedicated SMS number (from Twilio, e.g. +13035551234)
        <input className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={smsNumber} onChange={(e) => setSmsNumber(e.target.value)} placeholder="blank = shared Levelheaded number" />
      </label>
      <label>
        Consultation length (min)
        <input type="number" min={15} max={240} className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={consultMin} onChange={(e) => setConsultMin(e.target.value)} placeholder="default" />
      </label>
      <label>
        Mediation length (min)
        <input type="number" min={15} max={240} className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1" value={mediationMin} onChange={(e) => setMediationMin(e.target.value)} placeholder="default" />
      </label>
      {source.source_type === 'court' && (
        <label className="block">
          <span className="text-xs font-medium text-gray-600">Divisions (comma-separated, e.g. Division H, Division T)</span>
          <input
            type="text"
            className="mt-0.5 w-full rounded-md border border-surface-border px-2 py-1 text-sm"
            value={divisions}
            onChange={(e) => setDivisions(e.target.value)}
            placeholder="Division H, Division T"
          />
        </label>
      )}
      {source.source_type === 'court' && (
        <label className="flex items-center gap-2" title="Unknown numbers texting the dedicated number get the guided intake conversation instead of the unmatched inbox">
          <input
            type="checkbox"
            checked={smsIntake}
            disabled={!smsNumber.trim()}
            onChange={(e) => setSmsIntake(e.target.checked)}
          />
          SMS intake on the dedicated number{!smsNumber.trim() ? ' (set a number first)' : ''}
        </label>
      )}
      <div className="sm:col-span-2">
        <button
          type="button"
          className="btn-secondary !py-1 !text-xs"
          onClick={() =>
            onSave({
              sms_from_number: smsNumber.trim(),
              intake_config: {
                ...intake,
                ...(slug ? { slug } : {}),
                sms_intake_enabled: smsIntake && !!smsNumber.trim(),
                divisions: divisions.split(',').map((d) => d.trim()).filter(Boolean),
                ...(mediationMin.trim() ? { mediation_duration_minutes: Number(mediationMin) } : {}),
                ...(consultMin.trim() || (intake.consult as Record<string, unknown> | undefined)
                  ? {
                      consult: {
                        ...((intake.consult as Record<string, unknown>) ?? {}),
                        ...(consultMin.trim() ? { duration_minutes: Number(consultMin) } : {}),
                      },
                    }
                  : {}),
              },
              billing_config: {
                ...billing,
                ...(feeCents ? { party_fee_cents: Number(feeCents) } : {}),
                invoice_trigger: trigger,
                ...(connect ? { stripe_connect_account_id: connect } : {}),
              },
            })
          }
        >
          Save {source.name}
        </button>
      </div>
    </div>
  );
}


/* ════════════════════════ Practices (Mediator OS tenants) ════════════════════════ */

function PracticesTab({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [practices, setPractices] = useState<AdminPractice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<{ invite_link: string; intake_url: string | null } | null>(null);
  const [attachFor, setAttachFor] = useState<string | null>(null);
  const [attachOrg, setAttachOrg] = useState('');
  const [allSources, setAllSources] = useState<Array<{ id: string; name: string }>>([]);
  useEffect(() => {
    adminListSources(token)
      .then((res) => setAllSources(res.sources.map((s) => ({ id: s.id, name: s.name }))))
      .catch(() => {});
  }, [token]);

  const load = useCallback(() => {
    adminListPractices(token)
      .then((res) => setPractices(res.practices))
      .catch((err) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError(err instanceof EngineError ? err.message : 'Could not load practices.');
      });
  }, [token, onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await adminCreatePractice(token, {
        name: name.trim(),
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim(),
        ...(slug.trim() ? { intake_slug: slug.trim() } : {}),
      });
      setCreated({ invite_link: res.invite_link, intake_url: res.intake_url });
      setName(''); setOwnerName(''); setOwnerEmail(''); setSlug('');
      load();
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not create the practice.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="card p-4">
        <div className="section-label">Invite a practice</div>
        <p className="mt-1 text-xs text-gray-500">
          A practice is a tenant: an outside mediator or firm running on the platform. The owner
          becomes a tenant admin of their own practice only — never platform staff. A solo
          mediator is a practice of one.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Practice name (e.g. Front Range Mediation)" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Intake slug (optional, e.g. frontrange)" value={slug} onChange={(e) => setSlug(e.target.value)} />
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Owner name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          <input className="rounded-md border border-surface-border px-3 py-2 text-sm" placeholder="Owner email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
        </div>
        <button type="button" className="btn-primary mt-3 !text-sm" disabled={busy || !name.trim() || !ownerName.trim() || !ownerEmail.trim()} onClick={() => void create()}>
          {busy ? 'Creating…' : 'Create practice & generate invite'}
        </button>
        {created && (
          <div className="mt-3 rounded-md border border-teal-200 bg-teal-50 p-3 text-xs">
            <div className="font-medium text-teal-900">Practice created. Send the owner this invite link (shown once):</div>
            <code className="mt-1 block break-all">{created.invite_link}</code>
            {created.intake_url && <div className="mt-1 text-teal-800">Their intake link: <code>{created.intake_url}</code></div>}
          </div>
        )}
        {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
      </section>

      <section className="card p-4">
        <div className="section-label">Practices</div>
        {practices.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No practices yet.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {practices.map((p) => (
              <li key={p.id} className="rounded-md border border-surface-border p-2.5 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{p.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${p.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{p.status}</span>
                  {p.owner && <span className="text-xs text-gray-500">{p.owner.display_name} · {p.owner.email}</span>}
                </div>
                {p.sources.length > 0 && (
                  <div className="mt-1 text-xs text-gray-500">
                    Sources: {p.sources.map((s) => s.slug ? `${s.name} (/intake/${s.slug})` : s.name).join(', ')}
                  </div>
                )}
                {attachFor === p.id ? (
                  <div className="mt-2 flex items-center gap-2">
                    <select value={attachOrg} onChange={(e) => setAttachOrg(e.target.value)}
                      className="rounded-md border border-surface-border px-2 py-1 text-xs">
                      <option value="">Pick a source…</option>
                      {allSources.filter((s) => !p.sources.some((ps) => ps.id === s.id)).map((s) => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <button type="button" className="btn-primary !py-1 !text-xs" disabled={busy || !attachOrg}
                      onClick={() => { setBusy(true); adminAttachPracticeSource(token, p.id, attachOrg).then(() => { setAttachFor(null); setAttachOrg(''); load(); }).catch((e) => setError(e instanceof Error ? e.message : 'Attach failed')).finally(() => setBusy(false)); }}>
                      Attach
                    </button>
                    <button type="button" className="btn-secondary !py-1 !text-xs" onClick={() => setAttachFor(null)}>Cancel</button>
                  </div>
                ) : (
                  <button type="button" className="mt-2 text-xs font-medium text-brand underline" onClick={() => setAttachFor(p.id)}>
                    Attach existing source
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
